import {
  DEFAULT_INTEGRAL_COVERAGE,
  type IntegralCoverage,
} from "./integralCoverage";
import { INSURERS } from "./insurers";
import type { BenchInput } from "./types";

// Inputs stay strings while the merchant types.
export type BenchRow = {
  id: number;
  insurer: string;
  contractCount: string;
  unit: "count" | "percent";
  rate: string;
  includesVat: boolean;
  // Not part of the quote: it does not affect the price.
  integralCoverage: IntegralCoverage;
};

let nextId = 0;

export const newBenchRow = (): BenchRow => ({
  id: nextId++,
  insurer: INSURERS[0],
  contractCount: "",
  unit: "count",
  rate: "",
  includesVat: true,
  integralCoverage: DEFAULT_INTEGRAL_COVERAGE,
});

const isValidCount = (value: number) => Number.isInteger(value) && value >= 1;
const isValidRate = (value: number) => value > 0 && value <= 20;

export const rowContracts = (row: BenchRow, portfolio?: number): number =>
  row.unit === "percent"
    ? Math.max(
        1,
        Math.round(((portfolio ?? 0) * Number(row.contractCount)) / 100)
      )
    : Number(row.contractCount);

const isValidShare = (value: number) => value > 0 && value <= 100;

// Only a field the merchant has filled in can show an error.
export const rowErrors = (
  row: BenchRow
): { contractCount?: string; rate?: string } => ({
  contractCount:
    row.contractCount === ""
      ? undefined
      : row.unit === "percent"
      ? isValidShare(Number(row.contractCount))
        ? undefined
        : "Ingresa un porcentaje mayor a 0 y hasta 100%"
      : !isValidCount(Number(row.contractCount))
      ? "Ingresa un número entero mayor a 0"
      : undefined,
  rate:
    row.rate !== "" && !isValidRate(Number(row.rate))
      ? "Ingresa un precio mayor a 0 y hasta 20%"
      : undefined,
});

export const isBenchValid = (rows: BenchRow[], portfolio?: number): boolean =>
  rows.length > 0 &&
  rows.every(
    (row) =>
      (row.unit === "percent"
        ? isValidShare(Number(row.contractCount)) && !!portfolio
        : isValidCount(Number(row.contractCount))) &&
      row.contractCount !== "" &&
      isValidRate(Number(row.rate)) &&
      row.rate !== ""
  );

export const toBenchInput = (
  rows: BenchRow[],
  portfolio?: number
): BenchInput =>
  rows.map((row) => ({
    insurer: row.insurer,
    contractCount: rowContracts(row, portfolio),
    rate: Number(row.rate),
    includesVat: row.includesVat,
  }));
